import { makeStyles, useTheme } from '@material-ui/core/styles';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, createStyles, useMediaQuery } from '@material-ui/core';
import classnames from 'classnames';
import HtmlContent from '../../HtmlContent';

const useStyles = makeStyles(theme => createStyles({
    root: {
        position: 'relative',
        background: theme.palette.grey[100],
    },
    bullsEyeContainer: {
        display: 'flex',
        height: 550,
        alignItems: 'center',
        justifyContent: 'center',
        margin: theme.spacing(1),

        [theme.breakpoints.down('xs')]: {
            height: 360,
        },

        '&$small': {
            height: 360,
        },
    },
    small: {},
    readOnly: {},
    bullsEye: {
        position: 'relative',
        padding: 0,
        width: 500,
        height: 500,
        borderRadius: '50%',
        cursor: 'crosshair',

        [theme.breakpoints.down('xs')]: {
            width: 300,
            height: 300,
        },

        '$small &': {
            width: 272,
            height: 272,
        },

        '$readOnly &': {
            cursor: 'default',
        },
    },
    questionLabel: {
        position: 'absolute',
        zIndex: 2,
        color: 'black',
        wordBreak: 'break-word',
    },
    scoreCircle: {
        position: 'absolute',
        background: theme.palette.grey[200],
        border: theme.borders.primary,
        borderRadius: '50%',
    },
    line: {
        position: 'absolute',
        background: 'black',
        border: '1px solid black',
        height: '50%',
    },
    scorePoint: {
        height: 25,
        width: 25,
        backgroundColor: 'black',
        borderRadius: '50%',
        transform: 'translate(-50%, 50%)',
        position: 'absolute',

        [theme.breakpoints.down('xs')]: {
            width: 16,
            height: 16,
        },

        '$small &': {
            width: 16,
            height: 16,
        },
    },
}), { name: 'BullsEyeQuestion' });

const calculateScore = (xPos, yPos, width, numberOfCircles) => {
    const circleSize = (100 / numberOfCircles) / 100;
    const diagonalFromSenter = Math.sqrt((xPos ** 2) + (yPos ** 2));
    const radius = width / 2;
    let i;
    for (i = 0; i < numberOfCircles; i++) {
        const circleScoreWidth = circleSize * (i + 1);
        if (diagonalFromSenter <= circleScoreWidth * radius) {
            return numberOfCircles - i;
        }
    }
    return -1;
};

const questionSeparatorLineTopPositions = ['25%', '50%', '0%', '25%'];
const questionSeparatorLineLeftPositions = ['25%', '50%', '50%', '75%'];
const questionSeparatorLineTransform = ['rotate(90deg)', 'rotate(0deg)', 'rotate(0deg)', 'rotate(90deg)'];

const createLines = questions => questions.map((q, index) => ({
    style: {
        top: questionSeparatorLineTopPositions[index],
        left: questionSeparatorLineLeftPositions[index],
        transform: questionSeparatorLineTransform[index],
    },
    key: q.id,
}));

const createCircles = (numberOfCircles) => {
    const circles = [];
    const circleSize = 100 / numberOfCircles;
    let number = numberOfCircles;
    let i;
    for (i = 0; i < numberOfCircles; i++) {
        circles.push({
            style: {
                width: `${number * circleSize}%`,
                height: `${number * circleSize}%`,
            },
            key: i,
        });
        number -= 1;
    }
    return circles;
};

const labelPositionOffset = 24;
const labelPositionOffsetSmall = 12;

const getQuestionLabelPositionStyle = offset => ({
    0: { top: offset, right: offset },
    1: { top: offset, left: offset },
    2: { bottom: offset, left: offset },
    3: { bottom: offset, right: offset },
});

const getQuadrantPosition = (mouseClickX, mouseClickY, circleDiameter) => {
    const center = circleDiameter / 2;

    let index; let fromPosX; let fromPosY;
    if (mouseClickX > center && mouseClickY < center) {
        index = 0;
        fromPosX = mouseClickX - center;
        fromPosY = center - mouseClickY;
    } else if (mouseClickX < center && mouseClickY < center) {
        index = 1;
        fromPosX = center - mouseClickX;
        fromPosY = center - mouseClickY;
    } else if (mouseClickX < center && mouseClickY > center) {
        index = 2;
        fromPosX = center - mouseClickX;
        fromPosY = mouseClickY - center;
    } else if (mouseClickX > center && mouseClickY > center) {
        index = 3;
        fromPosX = mouseClickX - center;
        fromPosY = mouseClickY - center;
    }
    return { index, fromPosX, fromPosY };
};

const BullsEyeQuestions = ({
    questions, handleResponseUpdate, responses, readOnly, small,
}) => {
    const classes = useStyles();
    const [pointPositions, setPointPositions] = useState({});

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const useSmallLayout = small || isMobile;

    if (questions?.length !== 4) return null;

    const numberOfCircles = questions[0].scaleConfig.max;

    const onSetPoint = (event) => {
        if (readOnly) return;

        const currentTargetRect = event.currentTarget.getBoundingClientRect();
        const circleDiameter = event.currentTarget.clientHeight;
        const mouseClickX = parseFloat(event.pageX - window.pageXOffset - currentTargetRect.left);
        const mouseClickY = parseFloat(event.pageY - window.pageYOffset - currentTargetRect.top);

        const quadrantPosition = getQuadrantPosition(mouseClickX, mouseClickY, circleDiameter);
        const score = calculateScore(quadrantPosition.fromPosX, quadrantPosition.fromPosY, circleDiameter, numberOfCircles);
        const question = questions[quadrantPosition.index];

        setPointPositions(prevState => ({
            ...prevState,
            [question.id]: {
                left: (mouseClickX / circleDiameter) * 100,
                bottom: ((circleDiameter - mouseClickY) / circleDiameter) * 100,
            },
        }));

        handleResponseUpdate(question)('scaleResponse', score);
    };

    const getPointPosition = (questionResponse) => {
        const scaleResponse = questionResponse.response.scaleResponse;
        if (!scaleResponse) {
            return null;
        }

        // if presice position exists, use this
        if (pointPositions[questionResponse.questionId]) {
            return {
                left: `${pointPositions[questionResponse.questionId].left}%`,
                bottom: `${pointPositions[questionResponse.questionId].bottom}%`,
            };
        }

        // Otherwise calculate center position for score
        const circleRadius = numberOfCircles;
        const circleDiameter = circleRadius * 2;
        const pointRadius = numberOfCircles - scaleResponse + 0.5; // half way between lines -> scaleResponse should be minimum 1 (not 0)

        const questionIndex = questions.findIndex(d => d.id === questionResponse.questionId);
        const angleInDegrees = 45 - (questionIndex * 90);
        const xPos = pointRadius * Math.sin(angleInDegrees * Math.PI / 180) + circleRadius;
        const yPos = pointRadius * Math.cos(angleInDegrees * Math.PI / 180) + circleRadius;

        return {
            left: `${(xPos / circleDiameter) * 100}%`,
            bottom: `${(yPos / circleDiameter) * 100}%`,
        };
    };

    const circles = createCircles(numberOfCircles);
    const lines = createLines(questions);
    const questionResponses = questions.map(q => ({ questionId: q.id, response: responses[q.id]?.response }))
        .filter(d => d.response);

    return (
        <div className={classes.root}>
            {questions.map((question, i) => (
                <HtmlContent key={question.id} html={question.question} className={classes.questionLabel} style={getQuestionLabelPositionStyle(useSmallLayout ? labelPositionOffsetSmall : labelPositionOffset)[i]} />
            ))}

            <div className={classnames(classes.bullsEyeContainer, { [classes.small]: useSmallLayout, [classes.readOnly]: readOnly })}>
                <Button className={classnames(classes.bullsEye)} variant="contained" onClick={onSetPoint} disableTouchRipple>
                    {circles.map(circle => (
                        <div key={circle.key} className={classes.scoreCircle} style={circle.style} />
                    ))}

                    {lines.map(line => (
                        <div key={line.key} className={classes.line} style={line.style} />
                    ))}

                    {questionResponses?.map(q => (
                        <div key={q.questionId} className={classes.scorePoint} style={getPointPosition(q)} />
                    ))}
                </Button>
            </div>
        </div>
    );
};

BullsEyeQuestions.propTypes = {
    questions: PropTypes.array.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    responses: PropTypes.object,
    readOnly: PropTypes.bool,
    small: PropTypes.bool,
};

BullsEyeQuestions.defaultProps = {
    responses: null,
    readOnly: false,
    small: false,
};

export default BullsEyeQuestions;
